import {
  CalendarMonth as CalendarIcon,
  Favorite as HeartIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { useQuery } from "@tanstack/react-query";
import {
  endOfDay,
  format,
  startOfDay,
  startOfMonth,
  subDays,
  subMonths,
} from "date-fns";
import React, { useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { activityApi } from "../../../../apis";
import {
  MetricValuesAggregationRequestDto,
  MetricValuesAggregationRequestDtoAggregateTypesEnum,
  MetricValuesAggregationRequestDtoIntervalEnum,
  MetricValuesAggregationRequestDtoMetricCodeEnum,
} from "../../../../libs/cellera-sdk";
import { useCelleraTheme } from "../../../../theme/useCelleraTheme";

type TimeRange = "1D" | "1W" | "1M" | "6M" | "1Y" | "CUSTOM";

interface Props {
  patientId: string;
}

const RestingHeartRateChart: React.FC<Props> = ({ patientId }) => {
  const { colors } = useCelleraTheme();
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRange>("1W");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  /** 🗓️ Date range calculation */
  const getDateRange = (timeRange: TimeRange, customDate?: Date) => {
    const endDate = endOfDay(new Date());
    const startDate = new Date();

    switch (timeRange) {
      case "CUSTOM":
        if (customDate) {
          startDate.setTime(customDate.getTime());
          startDate.setHours(0, 0, 0, 0);
          endDate.setTime(customDate.getTime());
          endDate.setHours(23, 59, 59, 999);
        }
        break;
      case "1D":
        startDate.setHours(0, 0, 0, 0);
        break;
      case "1W":
        startDate.setDate(endDate.getDate() - 7);
        break;
      case "1M":
        startDate.setDate(endDate.getDate() - 30);
        break;
      case "6M":
        startDate.setMonth(endDate.getMonth() - 6);
        break;
      case "1Y":
        startDate.setFullYear(endDate.getFullYear() - 1);
        break;
    }

    return {
      startDate: startOfDay(startDate).toISOString(),
      endDate: endDate.toISOString(),
    };
  };

  const { startDate, endDate } = getDateRange(
    selectedTimeRange,
    selectedDate || undefined
  );

  /** 📊 Fetch resting heart rate data */
  const { data: metricData, isLoading } = useQuery({
    queryKey: [
      "restingHeartRateChart",
      patientId,
      selectedTimeRange,
      selectedDate,
    ],
    queryFn: async () => {
      if (!patientId) return null;
      const interval =
        selectedTimeRange === "6M" || selectedTimeRange === "1Y"
          ? MetricValuesAggregationRequestDtoIntervalEnum.Monthly
          : MetricValuesAggregationRequestDtoIntervalEnum.Daily;

      const request: MetricValuesAggregationRequestDto = {
        metricCode: MetricValuesAggregationRequestDtoMetricCodeEnum.HeartRate,
        interval,
        aggregateTypes: [
          MetricValuesAggregationRequestDtoAggregateTypesEnum.LatestRecord,
        ],
        startDate,
        endDate,
        orderBy: "ASC",
      };

      const response = await activityApi.getMetricValuesAggregatedByPatientId({
        patientId,
        metricValuesAggregationRequestDto: request,
      });
      return response;
    },
    enabled: !!patientId,
    refetchOnWindowFocus: false,
  });

  /** 🧮 Chart data builder */
  const chartData = useMemo(() => {
    if (!metricData?.data) return [];

    const dataMap: Record<string, number> = {};
    for (const d of metricData.data) {
      const key = format(new Date(d.startDate), "yyyy-MM-dd");
      dataMap[key] = d.valueIntData?.latest ?? d.valueFloatData?.latest ?? 0;
    }

    const today = new Date();

    if (selectedTimeRange === "1D") {
      const key = format(today, "yyyy-MM-dd");
      const value = dataMap[key] ?? 0;
      return [
        {
          date: format(today, "MMM d, yyyy"),
          value,
        },
      ];
    }

    if (selectedTimeRange === "1W") {
      const days = [];
      for (let i = 6; i >= 0; i--) {
        const date = subDays(today, i);
        const key = format(date, "yyyy-MM-dd");
        days.push({
          date: format(date, "EEE").slice(0, 2), // Mo, Tu, We...
          value: dataMap[key] ?? 0,
        });
      }
      return days;
    }

    if (selectedTimeRange === "1M") {
      const days = [];
      for (let i = 30; i >= 0; i--) {
        const date = subDays(today, i);
        const key = format(date, "yyyy-MM-dd");
        days.push({
          date: format(date, "dd"),
          value: dataMap[key] ?? 0,
        });
      }
      return days;
    }

    if (selectedTimeRange === "6M") {
      const months = [];
      for (let i = 5; i >= 0; i--) {
        const date = subMonths(today, i);
        const key = format(startOfMonth(date), "yyyy-MM");
        const total = Object.keys(dataMap)
          .filter((d) => d.startsWith(key))
          .reduce((sum, k) => sum + (dataMap[k] ?? 0), 0);
        const count = Object.keys(dataMap).filter((d) =>
          d.startsWith(key)
        ).length;
        months.push({
          date: format(date, "MMM"),
          value: count > 0 ? total / count : 0, // Average for the month
        });
      }
      return months;
    }

    if (selectedTimeRange === "1Y") {
      const months = [];
      for (let i = 11; i >= 0; i--) {
        const date = subMonths(today, i);
        const key = format(startOfMonth(date), "yyyy-MM");
        const total = Object.keys(dataMap)
          .filter((d) => d.startsWith(key))
          .reduce((sum, k) => sum + (dataMap[k] ?? 0), 0);
        const count = Object.keys(dataMap).filter((d) =>
          d.startsWith(key)
        ).length;
        months.push({
          date: format(date, "MMM").charAt(0),
          value: count > 0 ? total / count : 0, // Average for the month
        });
      }
      return months;
    }

    if (selectedTimeRange === "CUSTOM" && selectedDate) {
      const key = format(selectedDate, "yyyy-MM-dd");
      const value = dataMap[key] ?? 0;
      return [
        {
          date: format(selectedDate, "MMM d, yyyy"),
          value,
        },
      ];
    }

    return [];
  }, [metricData, selectedTimeRange, selectedDate]);

  // Calculate average
  const average = useMemo(() => {
    if (!metricData || metricData?.data?.length === 0) return 0;
    const sum = chartData.reduce((acc, item) => acc + item.value, 0);
    return Math.round(sum / metricData.data.length);
  }, [chartData, metricData]);

  const ranges: TimeRange[] = ["1D", "1W", "1M", "6M", "1Y"];

  if (isLoading) {
    return (
      <Card sx={{ borderRadius: 2, border: "1px solid #E8E8E9", p: 2 }}>
        <Typography variant="body2">Loading heart rate data...</Typography>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        borderRadius: 2,
        border: "1px solid #C6C5D5",
        p: 2,
        width: "100%",
        height: 516,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent
        sx={{ p: 0, flex: 1, display: "flex", flexDirection: "column", pb: 0 }}
      >
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <HeartIcon sx={{ color: colors.primary.main, fontSize: 22 }} />
            <Typography
              variant="body1"
              fontWeight={500}
              color="text.primary"
              sx={{ fontSize: "0.875rem" }}
            >
              Heart Rate
            </Typography>
          </Box>

          {/* Calendar Date Picker */}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              open={isDatePickerOpen}
              onClose={() => {
                setIsDatePickerOpen(false);
                setAnchorEl(null);
              }}
              value={selectedDate}
              onChange={(newValue) => {
                setSelectedDate(newValue);
                setSelectedTimeRange("CUSTOM");
                setIsDatePickerOpen(false);
                setAnchorEl(null);
              }}
              maxDate={new Date()}
              slotProps={{
                popper: {
                  anchorEl: anchorEl,
                  placement: "bottom-end",
                  sx: {
                    zIndex: 1300,
                  },
                },
                textField: {
                  sx: { display: "none" },
                },
              }}
            />
            <IconButton
              ref={(el) => setAnchorEl(el)}
              onClick={(event) => {
                setIsDatePickerOpen(true);
                setAnchorEl(event.currentTarget);
              }}
            >
              <CalendarIcon sx={{ fontSize: 18, color: colors.primary.main }} />
            </IconButton>
          </LocalizationProvider>
        </Box>

        {/* Time range selector */}
        <Box
          display="flex"
          justifyContent="space-between"
          mb={1}
          sx={{
            padding: "4px",
            width: "100%",
            height: "40px",
            background: "#F6F6F6",
            borderRadius: "14px",
            alignItems: "center",
          }}
        >
          {ranges.map((range) => (
            <Button
              key={range}
              variant="text"
              onClick={() => setSelectedTimeRange(range)}
              sx={{
                p: "6px 8px",
                height: 32,
                minHeight: 32,
                bgcolor:
                  selectedTimeRange === range ? "#FFFFFF" : "transparent",
                border:
                  selectedTimeRange === range ? "1px solid #E8E8E9" : "none",
                borderRadius: "10px",
                flex: 1,
                textTransform: "none",
                fontWeight: 500,
                fontSize: "12px",
                color: "#000000",
                "&:hover": {
                  bgcolor:
                    selectedTimeRange === range
                      ? "#FFFFFF"
                      : "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              {range}
            </Button>
          ))}
        </Box>

        {/* Average Display */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: "0.75rem" }}
          >
            AVERAGE
          </Typography>
          <Typography variant="h6" fontWeight={600} color="text.primary">
            {average} BPM
          </Typography>
        </Box>

        {/* Chart */}
        <Box sx={{ flex: 1, width: "100%", mt: 1, mb: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#E8E8E9" strokeWidth={1} />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                interval={selectedTimeRange === "1M" ? 6 : 0}
              />
              <YAxis tickLine={false} axisLine={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  borderColor: "#E8E8E9",
                  borderRadius: "8px",
                }}
                formatter={(value: number) => [
                  `${Math.round(value)} BPM`,
                  "Heart Rate",
                ]}
              />
              <Line
                type="monotone"
                dataKey="value"
                stroke={colors.primary.main}
                strokeWidth={2}
                dot={{ fill: colors.primary.main, strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, fill: colors.primary.main }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

export default RestingHeartRateChart;
